import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldCheck,
  Check,
  Circle,
  ArrowRight,
  ExternalLink,
  DollarSign,
  Receipt,
  Lock,
  AlertCircle,
} from "lucide-react";
import FeeBreakdown from "@/components/FeeBreakdown";

/**
 * MarketplaceOnboarding — Stripe Connect Express KYC page.
 *
 * Flow:
 *   1. Sign in as a customer or business.
 *   2. Click "Continue with Stripe" — we call POST /marketplace/seller/onboard.
 *   3. Stripe redirects the user through their hosted KYC form.
 *   4. On return, `?done=1` triggers a status refresh.
 *   5. Once `charges_enabled` & `payouts_enabled` are true → done, back to Sell.
 */
export default function MarketplaceOnboarding() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ctaBusy, setCtaBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketplace/seller/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadStatus();
    if (params.get("done") === "1") {
      // Coming back from Stripe — nudge a re-fetch just in case webhook lags
      const t = setTimeout(loadStatus, 1200);
      return () => clearTimeout(t);
    }
  }, [user, params, loadStatus]);

  const startOnboarding = async () => {
    setCtaBusy(true);
    try {
      const { data } = await api.post("/marketplace/seller/onboard", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setCtaBusy(false);
    }
  };

  const openDashboard = async () => {
    setCtaBusy(true);
    try {
      const { data } = await api.post("/marketplace/seller/dashboard-link");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCtaBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <Lock size={32} className="mx-auto text-[#B31E1E] mb-3" />
        <h1 className="text-3xl font-black tracking-tight text-[#1B1614]">Sign in to sell</h1>
        <p className="mt-2 text-[#5C4E48]">You need an account to accept marketplace payments.</p>
        <Link to="/login">
          <Button className="mt-6 rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white">
            Sign in
          </Button>
        </Link>
      </div>
    );
  }

  const canListPaid = !!status?.can_list_paid;
  const hasAccount = !!status?.has_account;
  const detailsSubmitted = !!status?.details_submitted;
  const requirements = status?.requirements_due || [];

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="marketplace-onboarding">
      <Link to="/marketplace" className="text-xs font-mono text-[#8B7B73] hover:text-[#B31E1E]">
        ← Back to Marketplace
      </Link>

      <div className="mt-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] text-[#B31E1E] border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold tracking-wide">
          <ShieldCheck size={11} /> Seller setup
        </span>
        <h1 className="mt-4 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614] leading-[1.05]">
          Get paid on Servrevu.
        </h1>
        <p className="mt-4 text-[17px] text-[#5C4E48]">
          Complete Stripe&apos;s standard identity verification (KYC) once, and every marketplace sale routes to your bank account automatically. Servrevu keeps a{" "}
          {status?.platform_fee_bp
            ? `${(status.platform_fee_bp / 100).toFixed(1)}%`
            : "2%"}{" "}
          platform fee — the rest is yours.
        </p>
      </div>

      {/* Transparent fee schedule — always shown so sellers know exactly what they'll net. */}
      <div className="mt-6">
        <FeeBreakdown priceDollars={0} variant="info" />
      </div>

      {/* Status card */}
      <Card
        className={`mt-8 rounded-2xl border p-8 ${
          canListPaid
            ? "border-emerald-300 bg-emerald-50/50 shadow-[0_10px_40px_-15px_rgba(15,108,52,0.30)]"
            : "border-[#EAE3DE] bg-white shadow-[0_4px_24px_-8px_rgba(179,30,30,0.10)]"
        }`}
        data-testid="onboarding-status-card"
      >
        {loading ? (
          <p className="text-sm text-[#8B7B73]">Loading status…</p>
        ) : canListPaid ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                  <Check size={16} />
                </span>
                <div>
                  <div className="font-semibold text-[#1B1614]">
                    You&apos;re verified and ready to sell.
                  </div>
                  <div className="text-xs text-[#5C4E48] font-mono">
                    Charges enabled · Payouts enabled
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={openDashboard}
                disabled={ctaBusy}
                variant="outline"
                className="rounded-lg border-[#EAE3DE] bg-white hover:bg-[#FAF5F2]"
                data-testid="onboarding-dashboard-button"
              >
                <ExternalLink size={13} className="mr-1.5" /> Stripe dashboard
              </Button>
              <Link to="/marketplace/sell">
                <Button
                  className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white font-semibold"
                  data-testid="onboarding-list-item-button"
                >
                  List an item <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              {hasAccount ? (
                <Badge className="rounded-full bg-amber-500 hover:bg-amber-500 text-white border-0 font-mono text-[10px]">
                  In progress
                </Badge>
              ) : (
                <Badge className="rounded-full bg-[#B31E1E] hover:bg-[#B31E1E] text-white border-0 font-mono text-[10px]">
                  Not started
                </Badge>
              )}
              {detailsSubmitted && !canListPaid && (
                <span className="text-xs text-[#8B7B73] font-mono">
                  Stripe is reviewing your submission
                </span>
              )}
            </div>

            <ul className="space-y-3 mb-6">
              <StepItem done={hasAccount} label="Create your Servrevu Seller account" />
              <StepItem done={detailsSubmitted} label="Complete Stripe identity verification (KYC)" />
              <StepItem done={canListPaid} label="Enable payouts to your bank" />
            </ul>

            {requirements.length > 0 && (
              <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="font-semibold flex items-center gap-1.5">
                  <AlertCircle size={14} /> Stripe still needs:
                </div>
                <ul className="mt-1.5 ml-6 list-disc space-y-0.5">
                  {requirements.slice(0, 8).map((r) => (
                    <li key={r} className="font-mono text-xs">{r}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={startOnboarding}
                disabled={ctaBusy}
                className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white font-semibold h-11 px-5"
                data-testid="onboarding-start-button"
              >
                {ctaBusy
                  ? "Redirecting to Stripe…"
                  : hasAccount
                  ? "Continue on Stripe"
                  : "Continue with Stripe"}
                <ArrowRight size={14} className="ml-1.5" />
              </Button>
              {hasAccount && (
                <Button
                  onClick={openDashboard}
                  disabled={ctaBusy}
                  variant="outline"
                  className="rounded-lg border-[#EAE3DE] bg-white hover:bg-[#FAF5F2]"
                  data-testid="onboarding-dashboard-button"
                >
                  <ExternalLink size={13} className="mr-1.5" /> Open Stripe dashboard
                </Button>
              )}
            </div>
          </>
        )}
      </Card>

      {/* Benefits row */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            Icon: DollarSign,
            title: "Automatic payouts",
            body: "Sales land in your bank daily — no invoicing, no waiting.",
          },
          {
            Icon: ShieldCheck,
            title: "Buyer protection built-in",
            body: "Stripe handles fraud checks and disputes so you focus on selling.",
          },
          {
            Icon: Receipt,
            title: "Tax-time ready",
            body: "Downloadable 1099-Ks and transaction history from your Stripe dashboard.",
          },
        ].map((b) => (
          <div
            key={b.title}
            className="rounded-xl border border-[#EAE3DE] bg-white p-5"
          >
            <div className="w-10 h-10 rounded-lg bg-[#F9E4E1] flex items-center justify-center mb-3">
              <b.Icon size={18} className="text-[#B31E1E]" />
            </div>
            <div className="font-semibold text-[#1B1614]">{b.title}</div>
            <p className="mt-1 text-sm text-[#5C4E48] leading-relaxed">{b.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 text-xs text-[#8B7B73]">
        You&apos;ll need a valid US bank account, government-issued ID, and your SSN (last 4) or EIN to verify. Stripe handles all of this — Servrevu never sees your sensitive data.
      </div>
    </div>
  );
}

function StepItem({ done, label }) {
  return (
    <li className="flex items-center gap-3">
      {done ? (
        <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
          <Check size={13} />
        </span>
      ) : (
        <Circle size={22} className="text-[#EAE3DE] shrink-0" />
      )}
      <span className={`text-sm ${done ? "text-[#5C4E48] line-through" : "text-[#1B1614] font-medium"}`}>
        {label}
      </span>
    </li>
  );
}
